/**
 * Performs the writes buildAttachOperations produced, against one ride.
 *
 * Kept free of imports on purpose: the API modules it is driven with are
 * injected, so this — the part that decides what counts as success — can be
 * exercised by the plain-node unit runner. src/services/attachParsedDocuments.js
 * is the thin wrapper that supplies the real endpoints.
 *
 * One failure never aborts the run: a voucher the server rejects must not cost
 * the manager the other nine documents they uploaded.
 *
 * Failures are reported in TWO lists, and the split is load-bearing:
 *
 *   `errors`          stays exactly what it has always been — the stay and
 *                     place writes. documentImportCreateRide reads only this,
 *                     so the create-a-new-trip flow keeps behaving as it
 *                     always has.
 *   `extraInfoErrors` day notes and trip-level general-information sections.
 *                     These were historically silent, and the create flow
 *                     still never looks at them; the attach-to-an-existing-ride
 *                     flow does, because there a swallowed failure meant the
 *                     panel said "1 document added" over notes that never
 *                     reached the trip.
 *
 * What is NOT negotiable either way: a failed write is never recorded as a
 * success. `succeededResultIndexes` feeds the duplicate protection and the
 * "N documents added" line, so a document whose day note was rejected stays
 * staged and retryable instead of being quietly retired.
 *
 * `failedGeneralInfoSections` carries the positions, within the generalInfo
 * array the operations were built from, of the sections whose POST did not
 * land — the only thing that makes them retryable, since they belong to no
 * card and nothing else remembers them.
 */

const { createAttachOutcomes } = require('./attachOperationOutcomes');

async function runAttachOperations({ apptoken, rideId, operations, api }) {
  const errors = [];
  const extraInfoErrors = [];
  const failedGeneralInfoSections = [];
  const outcomes = createAttachOutcomes();

  for (const op of operations || []) {
    if (op.kind === 'accommodation') {
      try {
        const response = await api.createAccommodation(apptoken, rideId, op.payload);
        if (response.data.ServerMassageCode !== 'success') {
          errors.push({
            name: op.sourceName,
            error: response.data.ServerMassageCode,
            resultIndex: op.resultIndex,
            kind: op.kind,
          });
          outcomes.record(op, false);
        } else {
          outcomes.record(op, true);
        }
      } catch (_) {
        errors.push({
          name: op.sourceName, error: 'unknown_error', resultIndex: op.resultIndex, kind: op.kind,
        });
        outcomes.record(op, false);
      }
    } else if (op.kind === 'attraction') {
      try {
        await api.createAttraction(apptoken, rideId, op.payload);
        outcomes.record(op, true);
      } catch (_) {
        errors.push({
          name: op.sourceName, error: 'unknown_error', resultIndex: op.resultIndex, kind: op.kind,
        });
        outcomes.record(op, false);
      }
    } else if (op.kind === 'extraInfo') {
      try {
        await api.addExtraInformation(apptoken, { ...op.payload, ride_id: rideId });
        outcomes.record(op, true);
      } catch (_) {
        extraInfoErrors.push({
          name: op.sourceName, error: 'unknown_error', resultIndex: op.resultIndex, kind: op.kind,
        });
        outcomes.record(op, false);
        if (typeof op.sectionIndex === 'number') failedGeneralInfoSections.push(op.sectionIndex);
      }
    }
  }

  return {
    errors,
    extraInfoErrors,
    succeededResultIndexes: outcomes.succeededResultIndexes(),
    failedGeneralInfoSections,
  };
}

module.exports = { runAttachOperations };
